import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatSeconds } from 'utils/formatSecond';
import { ERoute } from 'enums/route.enum';
import styled from 'styled-components';
import ChevronDown from './chevron-down.svg';
import Clock from './clock.svg';
import Video from './video.svg';

export interface LectureChapter {
  id: number;
  order: number;
  title: string;
  sessions: LectureSession[];
}

export interface LectureSession {
  id: number;
  order: number;
  title: string;
  runningTime: number;
  vimeoId?: string;
}

interface VideoLectureIndexChapterProps {
  chapter: LectureChapter;
  isOpen: boolean;
  onClickChapterTitle: () => void;
  currentChapterOrder: number;
  currentSessionOrder: number;
}

function VideoLectureIndexChapter({
  chapter,
  isOpen,
  onClickChapterTitle: handleClickChapterTitle,
  currentChapterOrder,
  currentSessionOrder,
}: VideoLectureIndexChapterProps) {
  const lecturesDOMRef = useRef<HTMLUListElement>(null);
  const [lecturesListHeight, setLecturesListHeight] = useState<number>(0);
  const numberOfVideos = chapter.sessions.length;

  const totalRunningTimeInSecond = chapter.sessions.reduce((sum, lecture) => sum + lecture.runningTime, 0);
  const runningTime = formatSeconds(totalRunningTimeInSecond);

  useEffect(() => {
    if (lecturesDOMRef.current) setLecturesListHeight(lecturesDOMRef.current?.clientHeight);
  }, []);

  return (
    <StyledContainer $lecturesListHeight={lecturesListHeight}>
      <button onClick={handleClickChapterTitle}>
        {/* Left */}
        <div className='left'>
          <span className='index-number'>{chapter.order}</span>
          <div className='chapter-info'>
            <h4 className='chapter-title'>{chapter.title}</h4>
            <div className='chapter-meta'>
              <div className='number-of-videos'>
                <img src={Video} alt='video' />
                <span>강의 총 {numberOfVideos}개</span>
              </div>
              <div className='running-time'>
                <img src={Clock} alt='clock' />
                <span>{runningTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className='right'>
          <img src={ChevronDown} alt={ChevronDown} data-isOpen={isOpen} />
        </div>
      </button>

      <div className='foldable' data-isOpen={isOpen}>
        <ul className='lectures' ref={lecturesDOMRef}>
          {chapter.sessions.map((session) => (
            <li key={session.title}>
              <Link
                className='lecture'
                to={{
                  pathname: ERoute.LectureInterviewMasterClassBasicVideoPlayer,
                  search: `chapter=${chapter.order}&session=${session.order}`,
                }}
                data-active={chapter.order === currentChapterOrder && session.order === currentSessionOrder}
              >
                <h6 className='lecture-name'>{session.title}</h6>
                <span className='lecture-running-time'>{formatSeconds(session.runningTime)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </StyledContainer>
  );
}

const StyledContainer = styled.article<{ $lecturesListHeight: number }>`
  background-color: white;
  border-radius: 4px;
  box-shadow: rgba(153, 153, 153, 0.08) 0px 2px 6px;

  > button {
    all: unset;
    box-sizing: border-box;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    column-gap: 10px;
    width: 100%;
    cursor: pointer;

    > .left {
      display: flex;
      column-gap: 6px;

      > .index-number {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 15px;
        height: 15px;
        margin: 1px;
        background-color: #003159;
        border-radius: 4px;
        color: white;
        font-size: 8.5px;
        font-weight: 600;
      }

      > .chapter-info {
        > .chapter-title {
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          line-height: 1.2;
          color: #000;
        }

        > .chapter-meta {
          font-size: 13px;
          font-weight: 400;
          color: #555;
          display: flex;
          column-gap: 12px;
          padding-top: 8px;
          line-height: 1.2;

          > .number-of-videos,
          .running-time {
            display: flex;
            align-items: center;
            column-gap: 4px;
          }
        }
      }
    }

    > .right {
      width: 20px;
      flex-shrink: 0;
      min-width: 20px;

      > img {
        width: 20px;
        height: 20px;
        transition: all 300ms;
        &[data-isOpen='false'] {
          transform: rotate(180deg);
        }
      }
    }
  }

  > .foldable {
    max-height: 0;
    overflow: hidden;
    transition: all 300ms;

    &[data-isOpen='true'] {
      max-height: ${({ $lecturesListHeight }) => $lecturesListHeight}px;
    }

    > .lectures {
      list-style: none;
      padding-left: 0;
      margin: 0 16px;
      padding: 16px 0;
      border-top: 1px solid #e8e8e8;
      display: grid;
      grid-template-columns: 1fr;
      row-gap: 4px;

      .lecture {
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: nowrap;
        padding: 0 14px;
        border-radius: 4px;
        column-gap: 16px;

        &[data-active='true'] {
          background-color: #f7f7f7;

          .lecture-name {
            color: #3cac40;
          }
        }

        .lecture-name {
          font-size: 13px;
          color: #555;
          font-weight: 500;
          margin: 0;
          line-height: 1.222;
        }

        .lecture-running-time {
          font-size: 13px;
          color: #aaa;
          font-weight: 400;
        }
      }
    }
  }
`;

export default VideoLectureIndexChapter;
